import { useState, useRef, useEffect, type MouseEvent as ReactMouseEvent } from 'react';
import { ChevronRight, ChevronLeft, Sparkles, CheckCircle2, Move, Circle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

type GrowStage = 'goal' | 'reality' | 'options' | 'wayforward';

interface StageData {
  title: string;
  titleColor: string;
  bgColor: string;
  dotColor: string;
  questions: string[];
  icon: string;
}

const stagesData: Record<GrowStage, StageData> = {
  goal: {
    title: 'Цель (Goal)',
    titleColor: 'text-white',
    bgColor: 'bg-[#FDF3CC]',
    dotColor: 'fill-goldBase',
    icon: '🎯',
    questions: [
      'Какую именно цель хочет достичь клиент?',
      'Как клиент измерит результат?',
      'Почему эта цель важна именно сейчас?',
      'Что изменится, когда цель будет достигнута?',
    ],
  },
  reality: {
    title: 'Реальность (Reality)',
    titleColor: 'text-white',
    bgColor: 'bg-[#FDEDEE]',
    dotColor: 'fill-redBase',
    icon: '🔍',
    questions: [
      'Где клиент находится сейчас по шкале от 0 до 10?',
      'Что уже было попробовано?',
      'Какие препятствия есть на пути?',
      'Что работает хорошо в текущей ситуации?',
    ],
  },
  options: {
    title: 'Возможности (Options)',
    titleColor: 'text-white',
    bgColor: 'bg-[#F1F2F8]',
    dotColor: 'fill-blackBase',
    icon: '💡',
    questions: [
      'Какие есть варианты решения?',
      'Кто мог бы помочь в этой ситуации?',
      'Что еще можно попробовать?',
      'Какие ресурсы доступны клиенту?',
    ],
  },
  wayforward: {
    title: 'Действия (Way Forward)',
    titleColor: 'text-white',
    bgColor: 'bg-[#EAF6EA]',
    dotColor: 'fill-[#5EDE6B]',
    icon: '🚀',
    questions: [
      'Какой первый шаг клиент сделает?',
      'Когда конкретно это произойдет?',
      'Как отследить прогресс?',
      'Что может помешать и как это преодолеть?',
    ],
  },
};

const stageOrder: GrowStage[] = ['goal', 'reality', 'options', 'wayforward'];

export interface GrowPanelProps {
  /** Встраивание в панель инструментов видеокомнаты: без fixed, без перетаскивания */
  embedded?: boolean;
  /** Плавающий режим: закрыть панель целиком (крестик в шапке) */
  onRequestClose?: () => void;
}

export function GrowPanel({ embedded = false, onRequestClose }: GrowPanelProps) {
  const [currentStage, setCurrentStage] = useState<GrowStage>('goal');
  const [completedStages, setCompletedStages] = useState<Set<GrowStage>>(new Set());
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);

  const currentData = stagesData[currentStage];
  const currentIndex = stageOrder.indexOf(currentStage);

  const goToNextStage = () => {
    if (currentIndex < stageOrder.length - 1) {
      setCompletedStages((prev) => new Set(prev).add(currentStage));
      setCurrentStage(stageOrder[currentIndex + 1]);
      setCurrentQuestionIndex(0);
    }
  };

  const goToPrevStage = () => {
    if (currentIndex > 0) {
      setCurrentStage(stageOrder[currentIndex - 1]);
      setCurrentQuestionIndex(0);
    }
  };

  const toggleStageComplete = () => {
    setCompletedStages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(currentStage)) {
        newSet.delete(currentStage);
      } else {
        newSet.add(currentStage);
      }
      return newSet;
    });
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < currentData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleMouseDown = (e: ReactMouseEvent) => {
    if (embedded) return;
    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y,
    };
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !dragRef.current) return;

    const deltaX = e.clientX - dragRef.current.startX;
    const deltaY = e.clientY - dragRef.current.startY;

    setPosition({
      x: dragRef.current.initialX + deltaX,
      // In floating mode the panel is anchored by `bottom`, so vertical drag
      // must invert delta: moving mouse down should decrease bottom offset.
      y: dragRef.current.initialY - deltaY,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    dragRef.current = null;
  };

  useEffect(() => {
    if (embedded || !isDragging) return;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [embedded, isDragging]);

  const outerClass = embedded
    ? 'h-full min-h-0 flex flex-col relative z-0'
    : 'fixed z-[2000] pointer-events-auto';

  const outerStyle = embedded
    ? undefined
    : {
        bottom: `calc(2rem + ${position.y}px)`,
        right: `calc(2rem - ${position.x}px)`,
        cursor: isDragging ? 'grabbing' : 'default',
      };

  const panelClass = embedded
    ? 'w-full max-w-full flex-1 min-h-0 flex flex-col bg-blackBase/55 backdrop-blur-xl rounded-[20px] shadow-shadowPopup border border-grey2/45 overflow-hidden'
    : 'w-[480px] max-w-[min(480px,calc(100vw-2rem))] bg-blackBase/55 backdrop-blur-xl rounded-[20px] shadow-shadowPopup border border-grey2/45 overflow-hidden';

  return (
    <div className={outerClass} style={outerStyle}>
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ x: embedded ? 0 : 400, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: embedded ? 0 : 400, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className={panelClass}
        >
          <div
            className={`bg-blackBase/40 p-2 flex items-center justify-between border-b border-grey2/45 ${
              embedded ? '' : 'cursor-grab active:cursor-grabbing'
            }`}
            onMouseDown={handleMouseDown}
          >
            <div className="flex items-center gap-2 min-w-0">
              {!embedded && <Move className="w-4 h-4 text-greyLightBlue shrink-0" />}
              <Sparkles className="w-4 h-4 text-redBase shrink-0" />
              <span className="text-white font-semibold text-sm truncate">GROW AI Coach</span>
            </div>
            {!embedded && onRequestClose && (
              <button
                type="button"
                aria-label="Закрыть"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onRequestClose();
                }}
                className="p-1 hover:bg-grey2/40 rounded-lg transition-colors shrink-0 cursor-pointer"
              >
                <X className="w-4 h-4 text-greyLightBlue hover:text-redBase" />
              </button>
            )}
          </div>

            <div className="p-2 border-b border-grey2/45 shrink-0 bg-blackBase/24">
              <div className="flex gap-2 mb-2">
                {stageOrder.map((stage) => (
                  <button
                    type="button"
                    key={stage}
                    onClick={() => {
                      setCurrentStage(stage);
                      setCurrentQuestionIndex(0);
                    }}
                    className={`flex-1 h-1.5 rounded-full transition-all cursor-pointer ${
                      stage === currentStage
                        ? stagesData[stage].bgColor
                        : completedStages.has(stage)
                          ? 'bg-greenLight/50'
                          : 'bg-grey2/50'
                    }`}
                  />
                ))}
              </div>

              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-lg shrink-0">{currentData.icon}</span>
                  <h3 className={`font-semibold text-sm truncate ${currentData.titleColor}`}>{currentData.title}</h3>
                </div>
                <div className="text-xs text-greyLightBlue shrink-0">
                  {currentIndex + 1} / {stageOrder.length}
                </div>
              </div>
            </div>

            <div className="p-3 flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden min-w-0 bg-blackBase/16">
              <div className="flex items-center justify-between mb-3 shrink-0">
                <p className="text-xs text-greyLightBlue">
                  Вопрос {currentQuestionIndex + 1} из {currentData.questions.length}
                </p>
                <button
                  type="button"
                  onClick={toggleStageComplete}
                  className={`p-1 rounded transition-colors cursor-pointer ${
                    completedStages.has(currentStage) ? 'text-greenLight' : 'text-greyLightBlue hover:text-redBase'
                  }`}
                >
                  <CheckCircle2 className="w-4 h-4" />
                </button>
              </div>

              <div className={`relative w-full shrink-0 overflow-hidden ${embedded ? 'h-[9rem]' : 'h-[9rem]'}`}>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={`${currentStage}-${currentQuestionIndex}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    className="h-full flex items-center"
                  >
                    <div className="bg-blackBase/22 p-4 rounded-[14px] border border-grey2/40 w-full min-w-0 h-full overflow-y-auto">
                      <p
                        className={`text-white leading-relaxed break-words ${
                          embedded ? 'text-sm sm:text-base' : 'text-3xl'
                        }`}
                      >
                        {currentData.questions[currentQuestionIndex]}
                      </p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="flex items-center justify-center gap-1.5 mt-3 shrink-0">
                {currentData.questions.map((_, index) => (
                  <button
                    type="button"
                    key={index}
                    onClick={() => setCurrentQuestionIndex(index)}
                    className={`transition-all cursor-pointer ${
                      index === currentQuestionIndex ? 'w-2.5 h-2.5' : 'w-1.5 h-1.5 opacity-50'
                    }`}
                  >
                    <Circle
                      className={`w-full h-full ${
                        index === currentQuestionIndex ? currentData.dotColor : 'fill-grey2'
                      }`}
                    />
                  </button>
                ))}
              </div>

              <div className="flex gap-2 mt-3 shrink-0">
                <button
                  type="button"
                  onClick={goToPrevQuestion}
                  disabled={currentQuestionIndex === 0}
                  className="flex-1 py-1.5 px-3 text-sm bg-grey4 hover:bg-blackBase disabled:bg-grey4/50 disabled:text-greyLightBlue disabled:cursor-not-allowed text-white rounded-[10px] transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Предыдущий
                </button>
                <button
                  type="button"
                  onClick={goToNextQuestion}
                  disabled={currentQuestionIndex === currentData.questions.length - 1}
                  className="flex-1 py-1.5 px-3 text-sm bg-blackBase hover:text-redBase disabled:bg-grey4/50 disabled:text-greyLightBlue disabled:cursor-not-allowed text-white rounded-[10px] transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  Следующий
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-2 border-t border-grey2/45 flex gap-2 shrink-0 bg-blackBase/24">
              <button
                type="button"
                onClick={goToPrevStage}
                disabled={currentIndex === 0}
                className="flex-1 py-1.5 px-3 text-sm bg-grey4 hover:bg-blackBase disabled:bg-grey4/50 disabled:text-greyLightBlue disabled:cursor-not-allowed text-white rounded-[10px] transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
                Предыдущий этап
              </button>
              <button
                type="button"
                onClick={goToNextStage}
                disabled={currentIndex === stageOrder.length - 1}
                className="flex-1 py-1.5 px-3 text-sm bg-redBase hover:bg-redBaseWithBlur20 disabled:bg-grey4/50 disabled:text-greyLightBlue disabled:cursor-not-allowed text-white rounded-[10px] transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
              >
                Следующий этап
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
